import api from "../api";
import {ENUMS} from "../services/enums";
import {EntityCollection} from "../server/EntityCollection.class";
import {subscribeToChannel, unsubscribeToChannel} from "../services/pusher";
import store from "../store";

const state = () => ({
  filters: {
    // Make sure to update resetFilters
    followUp: {
      users: [],
      campaigns: null,
      temperatures: null,
      dealProbabilityScore: null,
      statuses: null,
      sequences: null,
      sequenceStatus: null,
      pastNextContact: 0,
      lastContactedDateRange: [],
      followUpDateRange: [],
    },
  },

  hasMoreRecordsToLoad: true,

  activeTab: 'manual', // ['manual', 'automated']
  pageSize: 50,
  lastPage: 0,
  sortByEnum: ENUMS.SortByRecordsInFollowUpEnum,
  sortBy: ENUMS.SortByRecordsInFollowUpEnum.NextFollowUp.value,

  lazyLoaderIsOn: true,
  lazyLoader: null,
  pendingLazyLoadedFollowUpSequenceIds: [],
  lazyLoadedFollowUpSequenceData: {
    /**
     *    1: record 1 data
     *    2: record 2 data
     *    3: record 3 data
     */
  },
});

const getters = {
  lazyLoadedFollowUpSequenceData: (state) => (sequenceId) => {
    return state.lazyLoadedFollowUpSequenceData[sequenceId];
  },
  hasMoreRecordsToLoad(state) {
    return state.hasMoreRecordsToLoad;
  },
  activeTab(state) {
    return state.activeTab;
  },
  pageSize(state) {
    return state.pageSize
  },
  queryString: (state, getters, rootState, rootGetters) => {
    let query = {
      page: state.lastPage,
      page_size: getters.pageSize,
    };

    query.sort_by = state.sortBy;

    let view = rootGetters["pipeline/selectedPipelineView"];
    if (view) {
      query.view = view.id;
    }

    let filters = state.filters.followUp;

    if (filters.users.length) {
      query.user_ids = filters.users;
    }

    if (filters.campaigns) {
      query.campaign_id = filters.campaigns;
    }

    if (filters.temperatures) {
      query.temperature = filters.temperatures;
    }

    if (filters.statuses) {
      query.status_id = filters.statuses;
    }

    // Automated Only
    if (filters.sequences) {
      query.FUS_template_id = filters.sequences;
    }

    if (filters.sequenceStatus) {
      query.FUS_status = filters.sequenceStatus;
    }

    // Manual Only
    if (filters.pastNextContact) {
      query.only_passed_due = filters.pastNextContact;
    }

    if (getters.activeTab === 'manual') {
      if (filters.lastContactedDateRange.length) {
        query.last_contacted_start_date = filters.lastContactedDateRange[0]
        query.last_contacted_end_date = filters.lastContactedDateRange[1]
      }

      if (filters.followUpDateRange.length) {
        query.next_follow_up_start_date = filters.followUpDateRange[0]
        query.next_follow_up_end_date = filters.followUpDateRange[1]
      }
    }

    return new URLSearchParams(query).toString();
  }
};

const mutations = {
  incrementLastPage(state) {
   state.lastPage++;
  },
  hasMoreRecordsToLoad(state, hasMoreRecordsToLoad) {
    state.hasMoreRecordsToLoad = hasMoreRecordsToLoad;
  },
  setOneFilter(state, [filterObject, filter, value]) {
    state.filters[filterObject][filter] = value;
    store.dispatch('updateUserAppFilter')
  },
  setUserFilters(state, filters) {
    // make sure this match the filters declared on state
    state.filters = filters
  },
  setSortBy(state, value) {
    state.sortBy = value;
  },
  resetFilters(state, filterObject) {
    // Make sure to update state default
    state.filters[filterObject] = {
      users: [],
      campaigns: null,
      temperatures: null,
      dealProbabilityScore: null,
      statuses: null,
      sequences: null,
      sequenceStatus: null,
      pastNextContact: 0,
      lastContactedDateRange: [],
      followUpDateRange: [],
    };

    store.dispatch('updateUserAppFilter')
  },
  turnOnLazyLoader(state) {
    state.lazyLoaderIsOn = true;
  },
  setLazyLoader(state, lazyLoader) {
    state.lazyLoader = lazyLoader;
  },
  teardown(state, rootState) {
    state.lazyLoader = null;
    state.lazyLoaderIsOn = false;
    state.pendingLazyLoadedFollowUpSequenceIds = [];
    state.lazyLoadedFollowUpSequenceData = {};
    state.lastPage = 0;
    state.records = new EntityCollection();
  },
  clearPendingLazyLoadedFollowUpSequenceIds(state) {
    state.pendingLazyLoadedFollowUpSequenceIds = [];
  },
  addFollowUpSequenceToPendingLazyLoadedFollowUpSequences(state, recordId) {
    state.pendingLazyLoadedFollowUpSequenceIds = [
      recordId,
      ...state.pendingLazyLoadedFollowUpSequenceIds
    ];
  },
  appendLazyLoadedFollowUpSequenceData(state, records) {
    state.lazyLoadedFollowUpSequenceData = {...records, ...state.lazyLoadedFollowUpSequenceData};
  },
  activeTab(state, activeTab) {
    state.activeTab = activeTab;
    state.lastPage = 0;
  }
};

const actions = {
  setup({commit, state, rootState, getters, dispatch}) {
    commit('turnOnLazyLoader');
    dispatch('loadLazyLoadedData');

    commit('hasMoreRecordsToLoad', true);
    if (state.activeTab === 'automated') {
      dispatch('recordsInAutomatedFollowUp/setup', null, {root: true});
    }
    if (state.activeTab === 'manual') {
      dispatch('recordsInManualFollowUp/setup', null, {root: true});
    }

    subscribeToChannel("followUpState", rootState.market.market.id);
  },
  tearDown({dispatch, commit, state, rootState, getters}) {
    if (state.activeTab === 'automated') {
      dispatch('recordsInAutomatedFollowUp/tearDown', null, {root: true});
    }
    if (state.activeTab === 'manual') {
      dispatch('recordsInManualFollowUp/tearDown', null, {root: true});
    }

    unsubscribeToChannel("followUpState", rootState.market.market.id);
    commit('teardown');
  },

  activeTab({commit, state, dispatch}, activeTab) {
    if (state.activeTab === 'automated') {
      dispatch('recordsInAutomatedFollowUp/tearDown', null, {root: true});
    }
    if (state.activeTab === 'manual') {
      dispatch('recordsInManualFollowUp/tearDown', null, {root: true});
    }

    commit('activeTab', activeTab);
    return dispatch('setup');
  },

  incrementLastPage({commit}) {
    commit('incrementLastPage');
  },

  loadLazyLoadedData({commit, state, rootState, getters, dispatch}) {
    if (state.lazyLoaderIsOn) {
      if (state.pendingLazyLoadedFollowUpSequenceIds.length === 0) {
        console.log('No data to load');
      } else {
        api.followUp.getDictionary(state.pendingLazyLoadedFollowUpSequenceIds).then((response) => {
          commit('appendLazyLoadedFollowUpSequenceData', response.data);
        });
        commit('clearPendingLazyLoadedFollowUpSequenceIds');
      }

      let lazyLoader = setTimeout(() => {
        dispatch('loadLazyLoadedData');
      }, 1000);
      commit('setLazyLoader', lazyLoader);
    }
  },
};

const FollowUpStateStore = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default FollowUpStateStore;
